import { dataContextGuard } from "../lib/dataContext";
import { isLegacyAuthMode } from "../lib/env";
import { supabase } from "../lib/supabase";

export type OfferSource = { page: number; bbox: number[]; text: string };
export type OfferFinding = { code: string; message: string; sources: OfferSource[]; position: string | null; severity?: "note" | "review" };
export type OfferPreview = {
  parser_kind?: "gc_offer_v1" | "pichler_offer_v1" | "danfoss_offer_v1";
  supplier_role?: "manufacturer_direct" | "manufacturer";
  sales_channel?: "wholesale_explicit" | "not_stated";
  seller?: string | null; history_comparison_blocker?: string;
  source_groups?: Array<{ ordinal: number; heading: OfferSource; subtotal: { value: string; source: OfferSource } | null; subtotal_scope: "all_positions" | "regular_positions" | "both" | "unresolved" }>;
  source_regions?: Array<{ kind: string; source: OfferSource }>;

  created_date?: string | null; valid_until?: string | null;
  sum_comparable?: boolean; sum_difference?: string | null;
  findings?: OfferFinding[];
  source_header?: { sales_channel_sources?: OfferSource[]; terms?: OfferSource[]; references: Array<{ label: string; raw: string; source: OfferSource }> };
  source_context?: OfferSource[];
  pdf_available?: boolean;
  selection_token?: string; source_sha256?: string; reference_basis?: "last" | "best";
  doc_number: string | null; doc_date: string | null; supplier: string; currency: string | null;
  reference_date: string; warnings: string[]; save_allowed: boolean;
  lines: Array<{
    position_kind?: "goods" | "service" | "information" | "unknown";
    inclusion?: "regular" | "excluded" | "unknown"; related_position?: string | null;
    comparison_context_sources?: OfferSource[];
    purchase_selection_allowed?: boolean; price_basis_note?: string;
    printed_position?: string | null; discount_percent?: string | null;
    currency?: string | null; alternative?: boolean; optional?: boolean;
    printed_unit_price?: string | null; price_basis?: string | null;
    lv_reference?: string | null; stock_flag?: string | null;
    source_evidence?: { unlabelled_codes: OfferSource[]; raw_rows: OfferSource[] };
    comparison_issue?: string | null; comparison_key?: string | null; source_note?: boolean; manufacturer_article_code?: string | null; comparison_warning?: string | null; line_pos: string; page_no: number | null; article_code: string | null; description: string;
    qty: string | null; uom: string | null; net_unit_price: string | null; line_net_amount: string | null;
    reference: null | { doc_id: string; doc_number: string; doc_date: string; line_pos: string; page_no: number | null; price: string };
  }>;
};

export async function previewOffer(file: File, signal: AbortSignal, basis: "last" | "best" = "last"): Promise<OfferPreview> {
  const checkContext = dataContextGuard();
  const headers: Record<string, string> = {};
  if (!isLegacyAuthMode()) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) headers.Authorization = `Bearer ${session.access_token}`;
  }
  const body = new FormData();
  body.append("file", file);
  checkContext();
  const response = await fetch(`/api/angebote/preview${basis === "best" ? "?basis=best" : ""}`, {
    method: "POST", credentials: "same-origin", headers, body, signal, cache: "no-store",
  });
  checkContext();
  if (!response.ok) {
    const error = await response.json().catch(() => null) as { detail?: unknown } | null;
    throw new Error(typeof error?.detail === "string" ? error.detail : "Das Angebot konnte nicht geprüft werden. Bitte versuche es erneut.");
  }
  const result = await response.json() as OfferPreview;
  checkContext();
  return result;
}
